<template>
  <div class="document-container">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Alias ID Report Results</div>
          </template>
          <template #actions v-if="results && !loading">
            <ul>
              <li class="p-narrow no-border" v-if="hasModule('PRSID:DWNLD:DOWNLOAD')">
                <div class="list-content">
                  <a @click.prevent="generateReport('XLSX')"
                    @keypress.enter.prevent="generateReport('XLSX')"
                    tabindex="0" role="button"
                  >
                    Generate EXCEL
                  </a>
                </div>
              </li>
              <li class="p-narrow no-border" v-if="hasModule('PRSID:DWNLD:DOWNLOAD')">
                <div class="list-content">
                  <a @click.prevent="generateReport('PDF')"
                    @keypress.enter.prevent="generateReport('PDF')"
                    tabindex="0" role="button"
                  >
                    Generate PDF
                  </a>
                </div>
              </li>
              <li class="p-narrow no-border" style="justify-content: center;">
                <a
                  class="is-primary"
                  v-if="isSpecificLevel"
                  @click.prevent="goToPreviousLevel()"
                  @keypress.enter.prevent="goToPreviousLevel()"
                  tabindex="0" role="button"
                >
                  <font-awesome-icon
                    :icon="['far', 'angle-double-left']"
                    style="font-size: 1.8rem;"
                    class="fa-2x"
                    title="Go back to Parent Level"
                  />
                </a>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <!-- Summary Header -->
      <es-profile-header v-if="!loading && summaryInfo" class="is-inline">
        <template #profile-header>
          <div class="columns">
            <div class="column">
              <div class="column-content">
                <div class="header-subtitle" v-if="summaryInfo.subtitle">
                  {{summaryInfo.subtitle}}
                </div>
                <div
                  class="header-title"
                  v-if="isAllLevel || (isSpecificLevel && summaryInfo.title === 'Multiple')"
                >
                  {{summaryInfo.title}}
                </div>
                <router-link
                  v-else-if="summaryInfo.title.length"
                  :to="{
                    path: `../../personProfile/${stateId}/overview`
                  }"
                  class="header-title has-text-primary">
                  {{summaryInfo.title}}
                </router-link>
                <div class="header-title no-results-header-title" v-else>
                  --
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #profile-overview>
          <div class="columns">
            <div class="column" v-if="isAllLevel">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.personCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  Person <br/> Count
                </div>
              </div>
            </div>
            <div class="column" v-if="isSpecificLevel">
              <div class="column-content">
                <div class="column-title">
                  {{ stateId }} {{ identifierLabel }}
                </div>
                <div class="column-subtitle">
                  Identifier
                </div>
              </div>
            </div>
            <div class="column">
              <div class="column-content">
                <div class="column-title">
                  {{ results.summary.aliasIDCount | displayCount }}
                </div>
                <div class="column-subtitle">
                  Alias ID <br/> Count
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-profile-header>
      <!-- Results -->
      <div class="content">
        <es-loader v-if="loading"/>
        <div v-else>
          <es-exception
            v-if="!results || !results.details"
            exceptionMessage="Customize criteria must be entered to view results."
            style="padding: 3rem; margin: 0"
          />
          <es-exception
            v-else-if="!results.details.length"
            exceptionMessage="No results were found."
            style="padding: 3rem; margin: 0"
          />
          <div v-else>
            <div class="table-container">
              <table class="table is-hoverable is-fullwidth">
                <es-sorter
                  :apiSorter="results.sorters[0]"
                  :columnHeaders="getColumnHeaders"
                  @updateSorter="updateSorter"
                />
                <tbody>
                  <tr v-for="(item, index) in results.details" :key="index">
                    <td v-if="isAllLevel">
                      {{ item.personInfo.stateID }}
                    </td>
                    <td v-if="isSpecificLevel">
                      {{ item.personInfo.stateID }}
                    </td>
                    <td>
                      <router-link
                        :to="{
                          path: `../../personProfile/${item.personInfo.stateID}/overview`
                        }"
                        class="has-text-primary"
                      >
                        {{ item.personInfo.lastName }},
                        {{ item.personInfo.firstName}} {{ item.personInfo.middleName || '' }}
                      </router-link>
                    </td>
                    <td>
                      <div>
                        <router-link
                          :to="{
                            name: 'PIDistrictInfoDetails',
                            params: { code: item.districtInfo.code  },
                          }"
                          class="has-text-weight-bold has-text-primary" style="font-size:14px"
                        >
                          {{ item.districtInfo.name }}
                        </router-link>
                        <span class="padding-left--small has-text-grey ">
                          ({{ item.districtInfo.code }})
                        </span>
                      </div>
                      <div v-if="item.schoolInfo">
                        <router-link
                          :to="{
                            name: 'PISchoolInfoDetails',
                            params: {
                              districtCode: item.districtInfo.code,
                              schoolCode: item.schoolInfo.code
                            },
                          }"
                          class="has-text-primary is-italic" style="font-size:12px"
                        >
                          {{ item.schoolInfo.name }}
                        </router-link>
                        <span class="padding-left--small has-text-grey">
                          ({{ item.schoolInfo.code }})
                        </span>
                      </div>
                    </td>
                    <td v-if="isAllLevel">
                      {{ item.aliasIDCount }}
                    </td>
                    <td v-if="isSpecificLevel">
                      <span class="has-text-weight-bold">
                          {{ item.aliasID }}
                      </span>
                      <br />
                      <span class="has-text-grey">
                          {{ item.sourceSystem }}
                      </span>
                    </td>
                    <td v-if="isSpecificLevel">
                      {{ item.personType }}
                    </td>
                    <td v-if="isSpecificLevel">
                      {{ item.active ? 'Active' : 'Inactive' }}
                    </td>
                    <td v-if="isSpecificLevel">
                      {{item.createdDate | date }}
                    </td>
                    <td v-if="isSpecificLevel">
                      {{item.lastUpdateDate | date }}
                    </td>
                    <td class="action-column has-text-right" v-if="isAllLevel">
                      <a
                          class="is-primary"
                          @click.prevent="goToNextLevel(item)"
                          @keypress.enter.prevent="goToNextLevel(item)"
                          tabindex="0" role="link">
                        <font-awesome-icon
                          :icon="['far', 'angle-double-right']"
                          style="font-size: 1.8rem;"
                          class="fa-2x"
                          title="View Details"
                        />
                    </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <es-pager :totalCount="results.totalCount" :pageSize="pageSize"></es-pager>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import { mapGetters } from 'vuex';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import EsProfileHeader from '@/components/EsProfileHeader.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsPager from '@/components/EsPager.vue';

// mixins
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';

// icons
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDoubleRight, faAngleDoubleLeft } from '@fortawesome/pro-regular-svg-icons';

// utils
import moment from 'moment';

library.add(
  faAngleDoubleRight,
  faAngleDoubleLeft,
);

export default {
  components: {
    EsHeader, EsLoader, EsException, EsProfileHeader, EsSorter, EsPager,
  },
  mixins: [
    PermissionManager,
    FieldLabels,
  ],
  props: {
    results: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    defaultPersonType: {
      type: String,
      default: '',
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    levelIndex: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    goToNextLevel(item) {
      this.$emit('nextLevel', item);
    },
    goToPreviousLevel() {
      this.$emit('previousLevel');
    },
    generateReport(type) {
      this.$emit('generateReport', type);
    },
    updateSorter(sorter) {
      this.$emit('updateSorter', { field: sorter.field, order: sorter.order });
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      LEVELS: {
        ALL: 0,
        SPECIFIC: 1,
      },
    };
  },
  computed: {
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
    ]),
    isAllLevel() {
      return this.levelIndex === this.LEVELS.ALL;
    },
    isSpecificLevel() {
      return this.levelIndex === this.LEVELS.SPECIFIC;
    },
    stateId() {
      if (this.results && this.results.id) {
        return this.results.id;
      }

      return this.summaryPersonInfo ? this.summaryPersonInfo.stateID : '';
    },
    stateIdLabel() {
      return this.getFieldLabelByPersonType('STATE_STUDENT_ID', 'ASSIGN', this.defaultPersonType);
    },
    identifierLabel() {
      return this.results && this.results.idType && this.results.idType[0] === 'L' && this.results && this.results.id ? 'Local ID' : this.stateIdLabel;
    },
    summaryPersonInfo() {
      return this.results && this.results.summary && this.results.summary.personInfo
        ? this.results.summary.personInfo
        : null;
    },
    summaryInfo() {
      if (!this.results || !this.results.summary) {
        return null;
      }

      const { summary, details } = this.results;
      let title = '';
      let subtitle = '';

      if (this.levelIndex === this.LEVELS.SPECIFIC) {
        subtitle = 'Alias ID List for';

        if (summary.personInfo) {
          title = (`${summary.personInfo.lastName}${summary.personInfo.firstName ? ', ' : ''}${summary.personInfo.firstName || ''} ${summary.personInfo.middleName || ''}`).trim();
        }

        return { title, subtitle };
      }

      subtitle = 'Person List for';
      title = 'Multiple Districts';

      if (details.length === 1) {
        const item = details[0];
        title = `${item.districtInfo.name} (${item.districtInfo.code}) - ${item.schoolInfo.name} (${item.schoolInfo.code})`;
      } else if (summary.districtInfo && summary.districtInfo.code !== 'Multiple' && summary.districtInfo.code !== 'All') {
        title = `${summary.districtInfo.name} (${summary.districtInfo.code})`;

        if (summary.schoolInfo && summary.schoolInfo.code !== 'Multiple' && summary.schoolInfo.code !== 'All') {
          title = `${title} - ${summary.schoolInfo.name} (${summary.schoolInfo.code})`;
        }
      }

      return { title, subtitle };
    },
    getColumnHeaders() {
      let columnHeaders = [];
      const locationColumn = {
        header: 'Location Info',
        title: 'Location Info',
        field: 'schoolInfo',
        order: null,
        sortable: true,
      };

      if (this.isAllLevel) {
        columnHeaders = [
          {
            header: this.stateIdLabel,
            title: this.stateIdLabel,
            field: 'stateID',
            order: null,
            sortable: true,
          },
          {
            header: 'Name',
            title: 'Name',
            field: 'lastName',
            order: null,
            sortable: true,
          },
          {
            header: 'Alias ID Count',
            title: 'Alias ID Count',
            field: 'aliasIDCount',
            order: null,
            sortable: true,
          },
          {
            header: 'Actions',
            title: 'Actions',
            field: null,
          },
        ];
      } else {
        columnHeaders = columnHeaders.concat([
          {
            header: this.stateIdLabel,
            title: this.stateIdLabel,
            field: 'stateID',
            order: null,
            sortable: true,
          },
          {
            header: 'Name',
            title: 'Name',
            field: 'lastName',
            order: null,
            sortable: true,
          },
          {
            header: 'ID Info',
            title: 'ID Info',
            field: 'aliasID',
            order: null,
            sortable: true,
          },
          {
            header: 'Person Type',
            title: 'Person Type',
            field: 'personType',
            order: null,
            sortable: true,
          },
          {
            header: 'Status',
            title: 'Status',
            field: 'active',
            order: null,
            sortable: true,
          },
          {
            header: 'Creation Date',
            title: 'Creation Date',
            field: 'createdDate',
            order: null,
            sortable: true,
          },
          {
            header: 'Last Update Date',
            title: 'Last Update Date',
            field: 'lastUpdateDate',
            order: null,
            sortable: true,
          },
        ]);
      }

      columnHeaders.splice(2, 0, locationColumn);

      return columnHeaders;
    },
  },
  filters: {
    date(date) {
      return moment(date).format('MM/DD/YYYY');
    },
    displayCount(count) {
      return count || 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  .padding-left--small
    padding-left: 0.25rem
  .max-width-15
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
  .table-container
    overflow-x: auto
  .no-results-header-title
    cursor: default
</style>
